import { units } from '../../../units';

const buildDefs = (sourceVariables, components) => ({
  itemDesignation: {
    type: ['string', 'null'],
    label: 'Component (item designation)',
    placeholder: 'Select component',
    components,
    valueKey: 'itemDesignation',
  },
  variables: {
    type: 'array',
    title: 'Signals',
    default: [],
    items: {
      type: 'object',
      properties: {
        variableId: {
          type: ['string', 'null'],
          title: 'Signal',
          placeholder: 'Select signal',
          noOptionsMessage: 'No signals found.',
          default: sourceVariables[0]?.id || null,
          anyOf:
            sourceVariables.length > 0
              ? sourceVariables.map((v) => ({
                  type: 'string',
                  enum: [v.id],
                  title: `[ ${v.sourceName || '-'} ] [ ${v.itemDesignation || '-'} ] ${v.name}`,
                }))
              : [{ type: 'null', enum: [null], title: 'None available' }],
        },
        unit: {
          type: 'string',
          title: 'Display unit',
          default: units[0].id,
          anyOf: units.map((u) => ({
            enum: [u.id],
            title: `${u.category}: ${u.name}`,
          })),
        },
        name: {
          type: 'string',
          title: 'Display name',
          placeholder: 'Default to reference designation',
          default: '',
        },
        maxDecimals: {
          type: 'integer',
          title: 'Max decimals',
          default: 3,
          enum: [0, 1, 2, 3, 4, 5],
        },
      },
    },
  },
});

const getPushPinSchema = (variables = [], components = []) => ({
  type: 'object',
  definitions: buildDefs(variables, components),
  required: ['itemDesignation'],
  properties: {
    itemDesignation: {
      $ref: '#/definitions/itemDesignation',
    },
    variables: {
      $ref: '#/definitions/variables',
    },
  },
});

const pushPinUiSchema = {
  itemDesignation: {
    'ui:field': 'componentPickerField',
  },
};

const getViewerPanelSchema = (
  panelSchema,
  variables = [],
  components = [],
  componentsWithModels = [],
  onPremise = false
) => {
  const activeOptions = [
    { enum: [0], title: 'None' },
    { enum: [1], title: 'All' },
    { enum: [2], title: 'Active' },
    { enum: [3], title: 'Active or All' },
  ];

  return {
    type: 'object',
    definitions: buildDefs(variables, components),
    properties: {
      display: {
        type: 'boolean',
        title: 'Display viewer',
        default: true,
      },
      height: {
        type: 'string',
        title: 'Hero section height',
        default: 'fit-content',
        anyOf: [
          { enum: ['fit-content'], title: 'Fit content' },
          { enum: ['half'], title: '1/2 screen' },
          { enum: ['two-thirds'], title: '2/3 screen' },
          { enum: ['full'], title: 'Fullscreen' },
        ],
      },
      tooltipOnHover: {
        type: 'boolean',
        title: 'Show tooltip on component hover',
        default: false,
      },
      showAreaObjects: {
        type: 'boolean',
        title: 'Show component area-type objects',
        default: false,
      },
      focusActiveComponent: {
        type: 'boolean',
        title: 'Hide all inactive components on select outside viewer',
        default: false,
      },
      isolate: {
        type: 'boolean',
        title: 'Hide non-relevant components (isolate)',
        default: true,
      },
      forceErrorPins: {
        type: 'boolean',
        title: 'Display push pins for components with errors',
        default: false,
      },
      showActivePushpinTooltip: {
        type: 'boolean',
        title: 'Display push pins tooltip of active component if pushpins enabled',
        default: false,
      },
      modelSourceComponent: {
        type: ['string', 'null'],
        label: 'Display models linked to a specific component',
        placeholder: 'Select component',
        isClearable: true,
        noOptionsMessage: 'No models linked to component',
        components: componentsWithModels,
      },
      modelSourceComponentDefaultModel: {
        type: ['string', 'null'],
        label: 'Default model from selected component',
        placeholder: 'Select model',
        isClearable: true,
        noOptionsMessage: 'No models available',
        dependsOnFormField: 'modelSourceComponent',
        components: componentsWithModels,
      },
      selectionType: {
        type: 'number',
        title: 'Selection (highlight) type',
        default: 1,
        anyOf: [
          { enum: [1], title: 'Mixed' },
          { enum: [2], title: 'Regular' },
          { enum: [3], title: 'Overlay' },
        ],
      },
      renderComponents: {
        type: 'number',
        title: 'Render mapped components',
        default: 0,
        anyOf: activeOptions,
      },
      highlightComponents: {
        type: 'number',
        title: 'Highlight components (takes precedence over overlays)',
        default: 0,
        anyOf: activeOptions,
      },
      overlayComponents: {
        type: 'number',
        title: 'Overlay components (status color)',
        default: 0,
        anyOf: activeOptions,
      },
      showPushPins: {
        type: 'number',
        title: 'Display push pins',
        default: 0,
        anyOf: activeOptions,
      },
      toolbarOptions: {
        isMulti: true,
        type: 'array',
        title: 'Toolbar options (highlighted options will be displayed)',
        uniqueItems: true,
        default: [],
        items: {
          type: 'string',
          anyOf: [
            { enum: ['measure'], title: 'Measure tool' },
            { enum: ['firstperson'], title: 'First person tool' },
            { enum: ['home'], title: 'Home tool' },
            { enum: ['settings'], title: 'Settings tool' },
          ],
        },
      },
      pushPins: {
        type: 'array',
        title: 'Push pins',
        hasMoveDown: false,
        hasMoveUp: false,
        items: {
          type: 'object',
          required: ['itemDesignation'],
          properties: {
            itemDesignation: {
              $ref: '#/definitions/itemDesignation',
            },
            variables: {
              $ref: '#/definitions/variables',
            },
          },
        },
      },
      componentTooltips: {
        type: 'array',
        title: 'Component Tooltips',
        hasMoveDown: false,
        hasMoveUp: false,
        items: {
          type: 'object',
          required: ['itemDesignation'],
          properties: {
            itemDesignation: {
              $ref: '#/definitions/itemDesignation',
            },
            displayName: {
              type: ['string', 'null'],
              title: 'Display name',
            },
            displayValue: {
              type: ['string', 'null'],
              title: 'Display value',
            },
            freeText: {
              type: ['string', 'null'],
              title: 'Free text',
            },
            variables: {
              $ref: '#/definitions/variables',
            },
          },
        },
      },
      panels: {
        type: 'array',
        ...(onPremise ? {} : { maxItems: 1 }),
        items: {
          ...panelSchema,
        },
      },
    },
  };
};

const getViewerPanelAdminSchema = (variables = [], components = []) => ({
  type: 'object',
  definitions: buildDefs(variables, components),
});

const viewerPanelUiSchema = {
  modelSourceComponent: {
    'ui:field': 'componentPickerField',
  },
  modelSourceComponentDefaultModel: {
    'ui:field': 'modelPickerField',
  },
  pushPins: {
    items: {
      itemDesignation: {
        'ui:field': 'componentPickerField',
      },
    },
  },
  componentTooltips: {
    items: {
      itemDesignation: {
        'ui:field': 'componentPickerField',
      },
    },
  },
  panels: {
    items: {
      type: {
        'ui:field': 'disabledSelectField',
        'ui:disabled': true,
      },
    },
  },
};

export {
  getPushPinSchema,
  pushPinUiSchema,
  getViewerPanelSchema,
  viewerPanelUiSchema,
  getViewerPanelAdminSchema,
};
